import { Assert } from "../../Assert";
import { Nullable, PartialNullable } from "../../Extensions";
import { ObjectHelper } from "../../Helpers/ObjectHelper";
import { StringHelper } from "../../Helpers/StringHelper";
import { $$, IJNativeEvent, JNative, JNativeStatic } from "../../JNative";
import { LinqArray } from "../../LinqArray";
import { PropertyChange } from "../../PropertyChange";
import { Property, PropertyBoolean, PropertyString } from "../../RadObject";
import { ApplicationInformationService } from "../../RadWindow";
import { WebApiClientService } from "../../Server/WebApiClientService";
import { ServiceManager } from "../../ServiceManager";
import { Utility } from "../../Utility";
import { NAMESPACE } from "./_Namespace";
import { IInputControl, InputControl, InputControlPropertyValues } from "./InputControl";

export interface IFileUploadInformation
{
    guid: Nullable<string>;
    fileName: string;
}

export type FileUploadPropertyValues =
    InputControlPropertyValues
    & {
        accept: string;
        allowMultipleFiles: boolean;
        hideCurrentNameElement: boolean;
        selectedFiles: Nullable<FileList>;
    };

export type FileUploadProperties = {
    [name in keyof FileUploadPropertyValues]: Property<FileUploadPropertyValues[name]>
};

export interface IFileUpload extends IInputControl, FileUploadProperties
{
}

export class FileUpload extends InputControl implements IFileUpload
{
    // #region Static

    public static override classNamespace = NAMESPACE + "FileUpload";
    public static override className = "FileUpload";

    // #region Private Methods

    private static validateFiles(files: Nullable<FileList>, allowMultipleFiles?: boolean, accept?: string)
    {
        if (!files || files.length === 0)
            throw new Error("No file selected");
        else if (!allowMultipleFiles && files.length > 1)
            throw new Error("Only one file is permitted");

        if (accept)
        {
            const
                extensions =
                    LinqArray.create(accept.split(","))
                        .selectMany(
                            item =>
                            {
                                item = (item ?? StringHelper.empty).trim();

                                if (item.startsWith("."))
                                    return [item.toLowerCase()];

                                if (item.indexOf("/") > -1)
                                {
                                    const
                                        itemParts = item.split("/");

                                    switch (itemParts[0])
                                    {
                                        case "image":
                                            {
                                                const
                                                    imageExtensions = [];

                                                if (itemParts[1] === "bmp" || itemParts[1] === "*")
                                                    imageExtensions.push(".bmp");

                                                if (itemParts[1] === "gif" || itemParts[1] === "*")
                                                    imageExtensions.push(".gif");

                                                if (itemParts[1] === "jpg" || itemParts[1] === "*")
                                                {
                                                    imageExtensions.push(".jpg");
                                                    imageExtensions.push(".jpeg");
                                                }

                                                if (itemParts[1] === "png" || itemParts[1] === "*")
                                                    imageExtensions.push(".png");

                                                return imageExtensions;
                                            }
                                    }
                                }

                                return [];
                            });

            for (let file of files)
            {
                if (!extensions.any(item => file.name?.toLowerCase().endsWith(item)))
                    throw new Error(`Only files with the following extensions are permitted: ${extensions.join(", ")}`);
            }
        }

        const
            applicationInformation = ServiceManager.getRequired(ApplicationInformationService),
            maxRequestBodySizeInBytes = applicationInformation.limits.maxRequestBodySizeInBytes ?? 30000000,
            maxRequestBodySizeInMegabytes = Math.floor(maxRequestBodySizeInBytes / 1024 / 1024);

        let
            totalFileSizeInBytes = 0,
            zeroByteFilename = null;

        for (let file of files)
        {
            totalFileSizeInBytes += file.size;
            if (!file.size) zeroByteFilename = file.name;
        }

        if (zeroByteFilename)
            throw new Error(`The file "${zeroByteFilename}" contains no data`);

        if (totalFileSizeInBytes > maxRequestBodySizeInBytes)
        {
            if (files.length)
                throw new Error(`The files must total maximum of ${maxRequestBodySizeInMegabytes}MB`);

            throw new Error(`The file must be a maximum of ${maxRequestBodySizeInMegabytes}MB`);
        }
    }

    // #endregion

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<FileUploadPropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                allowMultipleFiles: false,
                hideCurrentNameElement: false,
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Global Variables

    protected $$container?: JNative<HTMLElement>;
    protected $$currentNameElement?: JNative<HTMLElement>;
    public $$fileUploadElement?: JNative<HTMLInputElement>;
    protected $$fileUploadLabel?: JNative<HTMLElement>;
    protected $$fileUploadLabelNewFile?: JNative<HTMLElement>;

    // #endregion

    // #region Properties

    public accept!: PropertyString;
    public allowMultipleFiles!: PropertyBoolean;
    public hideCurrentNameElement!: PropertyBoolean;
    public selectedFiles!: Property<Nullable<FileList>>;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<FileUploadPropertyValues>)
    {
        super(propertyValues);

        this.addCssClass("rad-control-file-upload");

        // Trigger Property Changes
        this.accept.triggerChange();
        this.allowMultipleFiles.triggerChange();
        this.hideCurrentNameElement.triggerChange();
    }

    // #endregion

    // #region Public Methods

    public resetFileUploadElement()
    {
        Assert.isNotNullOrUndefined(this.$$fileUploadElement, "this.$$fileUploadElement");
        Assert.isNotNullOrUndefined(this.$$fileUploadLabelNewFile, "this.$$fileUploadLabelNewFile");

        const
            formElement = this.$$fileUploadElement.wrap("<form>").closest("form").get(0) as HTMLFormElement | undefined;

        formElement?.reset();
        this.$$fileUploadElement.unwrap();
        this.$$fileUploadLabelNewFile.text("No file chosen");
    }
    public async uploadFilesAsync2()
    {
        const
            selectedFiles = this.selectedFiles.get();

        if (!selectedFiles?.length)
            return;

        FileUpload.validateFiles(selectedFiles, this.allowMultipleFiles.get() ?? false, this.accept.get() ?? StringHelper.empty);

        const
            fileUploadInformation = this.valueGetAsObject(),
            requests = [],
            webApiClient = ServiceManager.getRequired(WebApiClientService);

        Assert.isNotNullOrUndefined(fileUploadInformation, "fileUploadInformation");

        for (let i = 0; i < selectedFiles.length; i++)
        {
            // TODO: Implement a progress bar
            const
                fileInformation = fileUploadInformation[i],
                formData = new FormData();

            formData.append("file[]", selectedFiles[i], selectedFiles[i].name);

            requests.push(
                webApiClient.createFileUploadRequest("file", "uploadfile/" + fileInformation.guid, formData)
                    .executeAsync()
            );
        }

        await Promise.all(requests);
    }
    public uploadFilesAsync()
    {
        const
            selectedFiles = this.selectedFiles.get();

        if (selectedFiles?.length)
        {
            const
                fileUploadInformation = this.valueGetAsObject(),
                requests = [];

            Assert.isNotNullOrUndefined(fileUploadInformation, "fileUploadInformation");

            for (let i = 0; i < selectedFiles.length; i++)
            {
                // TODO: Implement a progress bar
                const
                    fileInformation = fileUploadInformation[i],
                    formData = new FormData();

                formData.append("file[]", selectedFiles[i], selectedFiles[i].name);

                requests.push(
                    ServiceManager.getRequired(WebApiClientService)
                        .createFileUploadRequest("file", "uploadfile/" + fileInformation.guid, formData)
                        .executeAsync()
                );
            }

            return Promise.all(requests);
        }

        return Promise.resolve();
    }
    public valueGetAsObject(): Nullable<IFileUploadInformation[]>
    {
        const
            value = this.value.get();

        if (!value)
            return null;

        let
            obj = null;

        try { obj = ObjectHelper.fromJson(value); }
        catch (e) { /* Continue regardless of error */ }

        return obj;
    }

    // #endregion

    // #region Protected Methods

    protected _acceptChanged(change: PropertyChange<string>)
    {
        if (change.currentValue)
            this.$$fileUploadElement?.attr("accept", change.currentValue);
        else
            this.$$fileUploadElement?.removeAttr("accept");
    }
    protected _allowMultipleFilesChanged(change: PropertyChange<boolean>)
    {
        if (change.currentValue)
            this.$$fileUploadElement?.attr("multiple", "multiple");
        else
            this.$$fileUploadElement?.removeAttr("multiple");
    }
    protected _elementValueChanged()
    {
        if (!this.__processElementValueChanged)
            return;

        Assert.isNotNullOrUndefined(this.$$fileUploadElement, "this.$$fileUploadElement");
        Assert.isNotNullOrUndefined(this.$$fileUploadLabelNewFile, "this.$$fileUploadLabelNewFile");

        const
            data = this.$$fileUploadElement.dataGet("upload-information") as IFileUploadInformation[] | undefined;

        if (data)
            this.$$fileUploadLabelNewFile.html(data.map(item => item.fileName).join("<br />"));
        else
            this.$$fileUploadLabelNewFile.empty();

        this.value.set(this._elementValueGet());
    }
    protected _elementValueGet()
    {
        Assert.isNotNullOrUndefined(this.$$fileUploadElement, "this.$$fileUploadElement");

        const
            value = this.$$fileUploadElement.dataGet("upload-information");

        let
            json;

        if (!value)
            return null;

        try { json = ObjectHelper.toJson(value); }
        catch (e) { /* Continue regardless of error */ }

        if (!json)
            return null;

        return json;
    }
    protected _elementValueSet(value: any)
    {
        this.__processElementValueChanged = false;

        Assert.isNotNullOrUndefined(this.$$fileUploadElement, "this.$$fileUploadElement");

        if (!value)
            this.$$fileUploadElement.data("upload-information", null);
        else
        {
            let
                obj = null;

            try { obj = ObjectHelper.fromJson(value); }
            catch (e) { /* Continue regardless of error */ }

            this.$$fileUploadElement.data("upload-information", obj);
        }

        this.__processElementValueChanged = true;
    }
    protected _getFileUploadGuidAsync(fileUpload: IFileUploadInformation)
    {
        return (
            Utility.instance
                .newGuidAsync()
                .then(data =>
                {
                    fileUpload.guid = data;
                })
        );
    }
    protected _hideCurrentNameElementChanged(change: PropertyChange<boolean>)
    {
        if (change.currentValue)
            this.$$currentNameElement?.hide();
        else
            this.$$currentNameElement?.show();
    }
    protected _selectedFilesChanged(change: PropertyChange<any>)
    {
        const
            selectedFiles = change.currentValue;

        if (selectedFiles?.length)
        {
            const
                uploadInformation: IFileUploadInformation[] = [];

            for (let i = 0; i < selectedFiles.length; i++)
                uploadInformation.push({ guid: null, fileName: selectedFiles[i].name });

            Promise.all(uploadInformation.map(item => this._getFileUploadGuidAsync(item)))
                .then(() =>
                {
                    Assert.isNotNullOrUndefined(this.$$fileUploadElement, "self.$$fileUploadElement");

                    this.$$fileUploadElement.data("upload-information", uploadInformation);
                    this._elementValueChanged();
                });
        }
        else
        {
            Assert.isNotNullOrUndefined(this.$$fileUploadElement, "self.$$fileUploadElement");

            this.$$fileUploadElement.data("upload-information", null);
            this._elementValueChanged();
        }
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: FileUploadPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this.accept = new PropertyString(this, propertyValues.accept, this._acceptChanged);
        this.allowMultipleFiles = new PropertyBoolean(this, propertyValues.allowMultipleFiles, this._allowMultipleFilesChanged);
        this.hideCurrentNameElement = new PropertyBoolean(this, propertyValues.hideCurrentNameElement, this._hideCurrentNameElementChanged);
        this.selectedFiles = new Property<Nullable<FileList>>(this, "System.Object, System.Private.CoreLib", propertyValues.selectedFiles, this._selectedFilesChanged);

        this.text.overrideGet(next =>
        {
            const
                fileUploadInformation = this.valueGetAsObject();

            return fileUploadInformation ? fileUploadInformation.map(item => item.fileName).join("\r\n") : StringHelper.empty;
        })
    }

    protected override _bindEvents()
    {
        super._bindEvents();

        Assert.isNotNullOrUndefined(this.$$fileUploadElement, "this.$$fileUploadElement");

        this.$$fileUploadElement.on("change", { self: this }, this.onFileUploadElementChanged);
    }
    protected override _createElement()
    {
        const
            $$element = super._createElement();

        this.$$container = JNativeStatic.create$$({ nodeName: "div", attributes: { "class": "rad-container" } }).appendTo($$element);
        this.$$currentNameElement = JNativeStatic.create$$({ nodeName: "p" }).appendTo(this.$$container);
        this.$$fileUploadElement = $$<HTMLInputElement>(StringHelper.formatWithHtmlEncode("<input id=\"{0}-input\" type=\"file\" />", this.controlId)).appendTo(this.$$container);
        this.$$fileUploadLabel = $$(StringHelper.formatWithHtmlEncode("<label for=\"{0}-input\">Choose file</label>", this.controlId)).appendTo(this.$$container);
        this.$$fileUploadLabelNewFile = JNativeStatic.create$$("span", null, "No file chosen").appendTo(this.$$container);
        this.labelTargetId = this.$$fileUploadElement.attr("id") ?? undefined;

        return $$element;
    }
    protected override _valueChanged(change: PropertyChange<any>)
    {
        super._valueChanged(change);

        const
            text = this.text.get();

        if (text)
            this.$$currentNameElement?.html(text.replace(/\r\n/gi, "<br/>"));
        else
            this.$$currentNameElement?.empty();
    }
    protected override onDataSourceBeforeSaveChanges(evt: IJNativeEvent, eventData: any)
    {
        const
            self = evt.data.self as this;

        super.onDataSourceBeforeSaveChanges(evt, eventData);
        eventData.promises.push(self.uploadFilesAsync());
    }

    // #endregion

    // #region Event Handlers

    protected onFileUploadElementChanged(evt: IJNativeEvent)
    {
        const
            self = evt.data.self as this;

        Assert.isNotNullOrUndefined(self.$$fileUploadElement, "self.$$fileUploadElement");

        self.selectedFiles.set(self.$$fileUploadElement[0].files);
    }

    // #endregion

    // #endregion
}

